import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { Button } from 'primeng/button';
import { RelatorioContainer } from '@/shared/components/relatorios/components/relatorio-container/relatorio-container';
import { RelatorioFormBase } from '@/shared/components/relatorios/relatorio-form.base';
import { RelatorioJobFacade } from '@/shared/components/relatorios/relatorio-job-base';
import { DeclaracaoDadosEmpresaPjService } from '@/core/api/sicom-api/registro/prestador/relatorios/declaracao-dados-empresa-pj/declaracao-dados-empresa-pj.service';
import { ReportDeclaracaoDadosEmpresaPjRequest, ReportDeclaracaoDadosEmpresaPjResponse } from '@/core/api/sicom-api/registro/prestador/relatorios/declaracao-dados-empresa-pj/declaracao-dados-empresa-pj.model';
import { DeclaracaoDadosEmpresaPjJobService } from '@/core/services/declaracao-dados-empresa-pj-job.service';
import { PessoaService } from '@/shared/services/pessoa.service';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';

type Req = ReportDeclaracaoDadosEmpresaPjRequest;
type Resp = ReportDeclaracaoDadosEmpresaPjResponse;

/**
 * Relatório PDF assíncrono: Declaração de Dados da Empresa PJ (ficha de dados cadastrais).
 * Herda `RelatorioFormBase`: export/polling/download/erro vêm da base; aqui só resolvemos a
 * identificação do prestador (nuCrm/sgUf via `PessoaService`).
 */
@Component({
    selector: 'app-declaracao-dados-empresa-pj',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [AsyncPipe, Button, RelatorioContainer, CanDirective],
    templateUrl: './declaracao-dados-empresa-pj.html',
    styleUrl: './declaracao-dados-empresa-pj.scss'
})
export class ReportDeclaracaoDadosEmpresaPjComponent extends RelatorioFormBase<Req, Resp> {
    /** Gate do botao Gerar: o backend exige esta role em /reports/** do modulo. */
    protected override readonly roleGerar = PERMISSOES.prestadores.relatorios.gerar;

    private readonly relatorioService = inject(DeclaracaoDadosEmpresaPjService);
    private readonly pessoaService = inject(PessoaService);
    readonly jobService = inject(DeclaracaoDadosEmpresaPjJobService);

    /** Stream do job para o `app-relatorio-container` (preview/PDF) no template. */
    readonly job$ = this.jobService.job$;

    protected get jobFacade(): RelatorioJobFacade {
        return this.jobService;
    }

    /** Monta o request a partir do prestador selecionado; retorna null (e avisa) se faltar CRM/UF. */
    protected buildRequest(): Req | null {
        const pessoa = this.pessoaService.getPessoa();
        const pessoaReference = this.pessoaService.getPessoaReference();
        const nuCrm = Number(pessoa?.dadosGerais?.crm || pessoa?.crm || pessoa?.registro || 0);
        const sgUf = pessoa?.uf || pessoaReference?.uf || '';

        if (!nuCrm || !sgUf) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Atenção',
                detail: 'Selecione um prestador antes de gerar a declaração.'
            });
            return null;
        }

        return { nuCrm, sgUf };
    }

    protected exportar(request: Req) {
        return this.relatorioService.exportarPdf(request);
    }

    limparFormulario(): void {
        this.limparJob();
    }
}
